import React, { useEffect, useRef, useState } from "react";
import { fmtDate } from "@/lib/dt";
import { useI18n } from "@/contexts/I18nContext";

/**
 * Round 12 · S9 — DD-MMM-YYYY date input (EN + AR).
 * Batch 9 · A6 (F-60) — the box itself now shows and ACCEPTS DD-MMM-YYYY: the visible control is a text input the user
 * types into (09-Oct-2026; also 09/10/2026, 09.10.2026 or 2026-10-09 are read as that date), and a calendar button opens the
 * browser's native picker. Before, the native ``input[type=date]`` sat invisibly over the display and typing went into it in
 * the browser's own order (mm/dd/yyyy on en-US). The value stays ISO ``YYYY-MM-DD`` for storage, audit trails and validators.
 */
const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
const AR_MONTHS = { "يناير": 1, "فبراير": 2, "مارس": 3, "أبريل": 4, "ابريل": 4, "مايو": 5, "يونيو": 6, "يوليو": 7, "أغسطس": 8, "اغسطس": 8, "سبتمبر": 9, "أكتوبر": 10, "اكتوبر": 10, "نوفمبر": 11, "ديسمبر": 12 };

/** Parse what a person types into ISO YYYY-MM-DD, or null. Day-month-year order always (never month first). */
export function parseTypedDate(text) {
  const s = String(text || "").trim().replace(/[⁦-⁩‎‏]/g, "");
  if (!s) return null;
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);                       // ISO
  if (m) return iso(+m[1], +m[2], +m[3]);
  m = s.match(/^(\d{1,2})[-/. ]([A-Za-z]{3,9}|[؀-ۿ]+)[-/. ](\d{2,4})$/);   // 09-Oct-2026 / 9 October 2026 / Arabic month
  if (m) {
    const mon = MONTHS.indexOf(m[2].slice(0, 3).toLowerCase()) + 1 || AR_MONTHS[m[2]] || 0;
    return iso(year(m[3]), mon, +m[1]);
  }
  m = s.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})$/);                  // 09/10/2026 → day/month/year
  if (m) return iso(year(m[3]), +m[2], +m[1]);
  m = s.match(/^(\d{2})(\d{2})(\d{4})$/);                                  // 09102026
  if (m) return iso(+m[3], +m[2], +m[1]);
  return null;
}
function year(y) { const n = +y; return y.length === 2 ? 2000 + n : n; }
function iso(y, mo, d) {
  if (!y || y < 1 || y > 9999 || mo < 1 || mo > 12 || d < 1 || d > 31) return null;   // a real calendar date of any year: the PAGE says when the year is out of range (A12)
  const dt = new Date(Date.UTC(y, mo - 1, d));
  if (dt.getUTCMonth() !== mo - 1) return null;
  return `${y}-${String(mo).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

export default function DateInput({ value, defaultValue, onChange, "data-testid": testid,
                                      className, placeholder, ...rest }) {
  const nativeRef = useRef(null);
  const { lang } = useI18n();
  // Round 27 · X3 — uncontrolled callers (defaultValue + onBlur) keep working.
  const [inner, setInner] = useState(defaultValue || "");
  if (value === undefined) value = inner;
  const [text, setText] = useState(value ? fmtDate(value) : "");
  const [bad, setBad] = useState(false);
  useEffect(() => { setText(value ? fmtDate(value) : ""); setBad(false); }, [value]);
  const { onBlur, disabled, readOnly, ...others } = rest;
  const commit = (isoValue, e) => {
    setInner(isoValue || "");
    onChange && onChange(isoValue || "");
    if (onBlur && e) onBlur(isoValue || "");
  };
  const openPicker = () => {
    const el = nativeRef.current;
    if (!el) return;
    if (typeof el.showPicker === "function") {
      try { el.showPicker(); return; } catch (_) { /* fall through */ }
    }
    el.focus(); el.click();
  };
  const onTextBlur = (e) => {
    const raw = e.target.value.trim();
    if (!raw) { setBad(false); commit("", e); return; }
    const parsed = parseTypedDate(raw);
    if (parsed) { setBad(false); setText(fmtDate(parsed)); commit(parsed, e); }
    else { setBad(true); if (onBlur) onBlur(value || ""); }
  };
  // Batch 23 · Stage 2: the box is at least as wide as its placeholder ("Date the work was done", the Arabic text) and its
  // date, so neither is cut — the font is monospaced, so the width in ch is exact for Latin text (Arabic glyphs are wider)
  const ph = placeholder || "DD-MMM-YYYY";
  const phCh = Math.ceil(ph.length * (/[\u0600-\u06FF]/.test(ph) ? 1.35 : 1));
  const boxCh = Math.max(13, phCh + 3);
  return (
    <span className={`relative inline-flex max-w-full flex-wrap items-center gap-1 ${className || ""}`}
          data-testid={testid ? `${testid}-wrap` : undefined}>
      <input type="text" value={text} inputMode="numeric" dir="ltr"
             placeholder={ph} disabled={disabled} readOnly={readOnly} style={{ minWidth: `${boxCh}ch` }}
             onChange={(e) => { setText(e.target.value); setBad(false); }}
             onBlur={onTextBlur}
             onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.target.blur(); } }}
             className={`min-w-[9rem] rounded border px-2 py-1 font-mono text-[11px] ${bad ? "border-red-500" : ""}`}
             title={bad ? (lang === "ar" ? "تاريخ غير صالح — اكتبه بصيغة DD-MMM-YYYY (مثل 09-Oct-2026)" : "Not a date — type it as DD-MMM-YYYY (for example 09-Oct-2026)") : undefined}
             data-testid={testid ? `${testid}-display` : undefined}
             {...others} />
      <button type="button" onClick={openPicker} disabled={disabled || readOnly} tabIndex={-1}
              className="rounded border px-1 py-0.5 text-[11px] leading-none" aria-label={lang === "ar" ? "اختر من التقويم" : "Pick from the calendar"}
              data-testid={testid ? `${testid}-calendar` : undefined}>📅</button>
      {/* the native picker, kept for the calendar only (never typed into) */}
      <input ref={nativeRef} type="date" value={value || ""} tabIndex={-1} aria-hidden="true"
             onChange={(e) => { setBad(false); commit(e.target.value, e); }}
             className="absolute h-0 w-0 opacity-0" style={{ left: 0, bottom: 0 }}
             data-testid={testid} />
      {bad && <span className="text-[10px] text-red-600" data-testid={testid ? `${testid}-error` : undefined}>{lang === "ar" ? "DD-MMM-YYYY" : "DD-MMM-YYYY"}</span>}
    </span>
  );
}
